import { useForm } from 'react-hook-form';
import { useToast } from '@/hooks/use-toast';
import { ProductsService } from '@/services/products.service';
import { ProductFormData } from '@/@types/Products';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { AxiosErrorResponse } from '@/@types/Errors';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage, translateApiError } from '@/utils/translateApiError';

interface UseProductFormOptions {
  afterProductCreated?: (productId: string) => Promise<void>;
}

export const useProductForm = (options: UseProductFormOptions = {}) => {
  const { afterProductCreated } = options;
  const { toast } = useToast();
  const t = useTranslation('common').t;
  const navigate = useNavigate();

  const convertToCents = (value: any) => {
    if (!value) return null;
    const numericValue = parseFloat(value.toString().replace(/\D/g, '')) || 0;
    return numericValue;
  };

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    watch,
    getValues,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ProductFormData>({
    defaultValues: {
      product_type: '',
      name: '',
      offer_name: '',
      description: '',
      sales_page: '',
      page_url: '',
      support_url: '',
      privacy_terms_url: '',
      email_support: '',
      whatsapp_support: '',
      phone_prefix: '55',
      offer_payment_type: '',
      offer_price: 0,
      offer_currency: 'BRL',
      product_delivery: '',
      offer_recurring_interval: '',
      offer_trial_enabled: false,
      offer_trial_mode: '',
      offer_trial_price: null,
      offer_trial_interval: '',
      category_id: '',
      sku: '',
      product_email: '',
      product_password: '',
      product_url: '',
      product_extras: '',
      refund_deadline_days: 7,
    },
    mode: 'onChange',
  });

  const defaultValues = watch();

  const { mutate, isPending: isCreatingProduct } = useMutation({
    mutationFn: (data: ProductFormData) => ProductsService.createProduct(data),
    onSuccess: async ({ data }) => {
      const product = data.data;

      try {
        if (afterProductCreated) {
          await afterProductCreated(product.id);
        }
      } catch {
        toast({
          variant: 'error',
          description: t('regulatoryDocumentsUploadError'),
        });
      }

      toast({
        variant: 'success',
        description: t('product_successfully_created'),
      });

      navigate(`/products/edit-product/${product.id}/geral`);
    },
    onError: (error: AxiosError<AxiosErrorResponse>) => {
      // Trata erros de validação do Laravel
      if (error?.response?.data?.errors) {
        const errors = error.response.data.errors;
        Object.entries(errors).forEach(([key, messages]) => {
          const message = Array.isArray(messages) ? messages[0] : messages;
          setError(key as keyof ProductFormData, {
            type: 'manual',
            message: translateApiError(String(message), t),
          });
        });

        toast({
          variant: 'error',
          description: getApiErrorMessage(error, t),
        });
      } else {
        toast({
          variant: 'error',
          description: getApiErrorMessage(error, t),
        });
      }
    },
  });

  const onSubmit = async (data: ProductFormData) => {
    if (!defaultValues.product_delivery) {
      setError('product_delivery', { message: t('required_field') });
      return;
    }

    const { page_url, support_url, privacy_terms_url, offer_name, ...rest } =
      data;

    const cleanedPageUrl = page_url?.trim();
    const cleanedSupportUrl = support_url?.trim();
    const cleanedPrivacyUrl = privacy_terms_url?.trim();
    const cleanedOfferName = offer_name?.trim();

    const payload = {
      ...rest,
      whatsapp_support: rest.whatsapp_support,
      offer_price: convertToCents(data.offer_price),
      ...(cleanedOfferName ? { offer_name: cleanedOfferName } : {}),
      ...(cleanedPageUrl ? { page_url: cleanedPageUrl } : {}),
      ...(cleanedSupportUrl ? { support_url: cleanedSupportUrl } : {}),
      ...(cleanedPrivacyUrl ? { privacy_terms_url: cleanedPrivacyUrl } : {}),
    };

    mutate(payload);
  };

  return {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    watch,
    getValues,
    errors,
    onSubmit,
    isSubmitting,
    isCreatingProduct,
    reset,
  };
};
